import { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { ToolViewContent } from '../components/ToolViewContent';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CircleAlert, LockKeyhole } from 'lucide-react';

const TOOLS_LIST_PATH = '/traceability/tools';

export function ToolDetailPage() {
  const { toolId } = useParams<{ toolId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const http = useApiClient();
  const { t } = useTranslation();

  const traceabilityApiClient = useMemo<TraceabilityApiClient | null>(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const handleBackToList = () => {
    navigate(TOOLS_LIST_PATH);
  };

  const listBreadcrumb = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    { label: t('toolView.list.breadcrumb.tools'), onClick: () => navigate(TOOLS_LIST_PATH) },
  ];
  const breadcrumbItems = [...listBreadcrumb, { label: toolId || '' }];

  const backToListLink = (
    <Button asChild variant="outline" size="sm" className="gap-2">
      <Link to={TOOLS_LIST_PATH}>
        <ArrowLeft className="size-4" aria-hidden />
        {t('toolView.detail.actions.backToList')}
      </Link>
    </Button>
  );

  // Handle missing or invalid toolId
  if (!toolId || toolId.trim() === '') {
    return (
      <PageLayout>
        <div className="flex flex-col gap-6">
          <PageHeader breadcrumb={listBreadcrumb} title={t('toolView.detail.pageTitle')} />
          <EmptyState
            tone="error"
            icon={CircleAlert}
            title={t('toolView.detail.error.invalidToolId')}
            description={t('toolView.detail.error.noToolIdProvided')}
            action={backToListLink}
          />
        </div>
      </PageLayout>
    );
  }

  // Handle missing API client (authentication issue)
  if (!traceabilityApiClient) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-6">
          <PageHeader breadcrumb={listBreadcrumb} title={t('toolView.detail.pageTitle')} />
          <EmptyState
            icon={LockKeyhole}
            title={t('toolView.detail.error.authenticationRequired')}
            description={t('toolView.detail.error.initializingConnection')}
            action={backToListLink}
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          title={toolId}
          mono
          description={t('toolView.detail.description')}
          actions={
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleBackToList}
              aria-label={t('toolView.detail.actions.backToList')}
            >
              <ArrowLeft className="size-4" aria-hidden />
              {t('toolView.detail.actions.backToList')}
            </Button>
          }
        />

        <ToolViewContent
          toolId={toolId}
          traceabilityApiClient={traceabilityApiClient}
          editReference={searchParams.get('edit') === 'reference'}
        />
      </div>
    </PageLayout>
  );
}
